import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../common/LoadingSpinner';

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { requestOTP, verifyOTP, verificationStage, retriesLeft, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const result = await requestOTP(email.trim());
    if (!result.success) {
      setError(result.error);
    } else {
      if (result.agent) {
        // Demo mode - direct login
        navigate('/dashboard', { replace: true });
      } else {
        // OTP sent
        setMessage(result.message);
      }
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!otpCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    const result = await verifyOTP(otpCode.trim());
    if (!result.success) {
      setError(result.error);
      if (result.retriesLeft !== undefined) {
        setError(`${result.error} (${result.retriesLeft} attempts remaining)`);
      }
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F7FBFC] via-[#F7FBFC] to-[#F7FBFC] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Login Header */}
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-[#022b73]">
            Welcome
          </h2>
        </div>
        
        {verificationStage === 'email' ? (
          <form className="mt-8 space-y-6" onSubmit={handleEmailSubmit}>
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-3 border border-[#19BFB7]/30 placeholder-[#033EA5]/60 text-[#022b73] focus:outline-none focus:ring-2 focus:ring-[#19BFB7] focus:border-[#19BFB7] focus:z-10 sm:text-sm bg-[#F7FBFC]/80 backdrop-blur-sm"
                placeholder="Enter your registered email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg shadow-sm">
                {error}
              </div>
            )}

            {message && (
              <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded-lg shadow-sm">
                {message}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading || message}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-[#022b73] via-[#033EA5] to-[#011841] hover:from-[#033EA5] hover:to-[#022b73] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#19BFB7] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all duration-300"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : message ? (
                  'Code Sent'
                ) : (
                  'Login'
                )}
              </button>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleOTPSubmit}>
            <div className="text-center mb-4">
              <p className="text-sm text-[#033EA5]">
                We sent a 6-digit verification code to <strong>{email}</strong>
              </p>
            </div>

            <div>
              <label htmlFor="otpCode" className="sr-only">
                Verification Code
              </label>
              <input
                id="otpCode"
                name="otpCode"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-3 border border-[#19BFB7]/30 placeholder-[#033EA5]/60 text-[#022b73] focus:outline-none focus:ring-2 focus:ring-[#19BFB7] focus:border-[#19BFB7] focus:z-10 sm:text-sm bg-[#F7FBFC]/80 backdrop-blur-sm text-center text-lg tracking-widest"
                placeholder="123456"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                disabled={loading}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg shadow-sm">
                {error}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="flex-1 py-3 px-4 border border-[#19BFB7] text-[#022b73] rounded-lg hover:bg-[#19BFB7]/10 transition-colors"
              >
                ← Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-[#022b73] via-[#033EA5] to-[#011841] hover:from-[#033EA5] hover:to-[#022b73] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#19BFB7] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all duration-300"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  '🔐 Verify Code'
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-xs text-[#033EA5]">
                Attempts remaining: <strong>{retriesLeft}</strong>
              </p>
            </div>
          </form>
        )}

        {/* Moved and minimized warning message */}
        <div className="text-[8px] text-[#033EA5] text-center mt-8 opacity-70">
          <p>⚠️ AUTHORIZED ACCESS ONLY - For PLSSS Sales Team members only. All activities logged.</p>
        </div>
      </div>
    </div>
  );
}
